import axios from 'axios';
import { formatNewOpportunityPhoneNumber } from '../utils/newOpportunityPhone';

export type NewOpportunityExecutionStatus = 'SCHEDULED' | 'COMPLETED' | 'NO_SHOW';

export type NewOpportunityJournalExcludedReason =
    | 'STATUS_NO_SHOW'
    | 'POLICY_NOT_ENROLLED'
    | 'NOT_STARTED'
    | 'INACTIVE';

export type NewOpportunityDTO = {
    _id?: string;
    id?: string;
    newOpportunityId?: string;
    centerId?: string;
    coachId?: string;
    coachName?: string;
    memberId?: string;
    memberName?: string;
    memberPhoneNumber?: string;
    memberType?: string;
    categoryId?: string;
    categoryName?: string;
    name?: string;
    displayName?: string;
    memo?: string;
    startTime: string;
    endTime: string;
    updatedAt?: string;
    executionStatus?: NewOpportunityExecutionStatus;
    isNoShow?: boolean;
    appAccountStatus?: 'linked' | 'withdrawn' | 'unlinked';
    appWithdrawnAt?: string | null;
    appWithdrawalAuthStatus?: 'pending' | 'revoked' | null;
    isReadOnly?: boolean;
    memberAppLinked?: boolean;
    hasWorkoutJournal?: boolean;
    workoutJournalPostId?: string | null;
    canWriteWorkoutJournal?: boolean;
    canCreateWorkoutJournal?: boolean;
    canEditWorkoutJournal?: boolean;
    canDeleteWorkoutJournal?: boolean;
    journalAvailableFrom?: string;
    journalEligible?: boolean;
    journalExcludedReason?: NewOpportunityJournalExcludedReason;
};

export type NewOpportunityJournalResponse = {
    success: boolean;
    opportunity: NewOpportunityDTO;
    journal: Record<string, any> | null;
};

const buildUrl = (base: string, params: Record<string, string | undefined>) => {
    const qs = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(value || '')}`)
        .join('&');
    return qs ? `${base}?${qs}` : base;
};

const headers = (accessToken: string) => ({ authorization: accessToken });

export const getNewOpportunityCategoriesAPI = async (centerId: string, accessToken: string): Promise<any> => {
    const response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunity-categories?includeInactive=true`,
        headers: headers(accessToken),
    });
    return response;
};

export const createNewOpportunityCategoryAPI = async (
    centerId: string,
    accessToken: string,
    body: { name: string; sortOrder?: number }
): Promise<any> => {
    const response = await axios({
        method: 'post',
        url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunity-categories`,
        headers: headers(accessToken),
        data: body,
    });
    return response;
};

export const updateNewOpportunityCategoryAPI = async (
    centerId: string,
    categoryId: string,
    accessToken: string,
    body: { name?: string; sortOrder?: number; isActive?: boolean }
): Promise<any> => {
    const response = await axios({
        method: 'patch',
        url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunity-categories/${categoryId}`,
        headers: headers(accessToken),
        data: body,
    });
    return response;
};

export const deleteNewOpportunityCategoryAPI = async (
    centerId: string,
    categoryId: string,
    accessToken: string
): Promise<any> => {
    const response = await axios({
        method: 'delete',
        url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunity-categories/${categoryId}`,
        headers: headers(accessToken),
    });
    return response;
};

export const getNewOpportunitiesAPI = async (
    centerId: string,
    accessToken: string,
    params: {
        startTime?: string;
        endTime?: string;
        coachId?: string;
        memberId?: string;
        categoryId?: string;
        executionStatus?: NewOpportunityExecutionStatus;
        journalEligible?: 'true';
        asOf?: string;
        includeActiveMembers?: string;
        keyword?: string;
        page?: string;
        limit?: string;
    }
): Promise<any> => {
    const response = await axios({
        method: 'get',
        url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunities`, params),
        headers: headers(accessToken),
    });
    return response;
};

export const getNewOpportunityCalendarAPI = async (
    centerId: string,
    accessToken: string,
    params: {
        startTime?: string;
        endTime?: string;
        coachId?: string;
        categoryId?: string;
    }
): Promise<any> => {
    const response = await axios({
        method: 'get',
        url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunities/calendar`, params),
        headers: headers(accessToken),
    });
    return response;
};

export const getNewOpportunityMemberMatchAPI = async (
    centerId: string,
    accessToken: string,
    phoneNumber: string
): Promise<any> => {
    const response = await axios({
        method: 'get',
        url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunities/member-match`, {
            phoneNumber: formatNewOpportunityPhoneNumber(phoneNumber),
        }),
        headers: headers(accessToken),
    });
    return response;
};

export const createNewOpportunityAPI = async (
    centerId: string,
    accessToken: string,
    body: {
        coachId: string;
        memberId?: string;
        memberName: string;
        phoneNumber: string;
        categoryId: string;
        name: string;
        startTime: string;
        endTime: string;
        memo?: string;
        isSendAlimtalkToMember?: boolean;
    }
): Promise<any> => {
    const response = await axios({
        method: 'post',
        url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunities`,
        headers: headers(accessToken),
        data: {
            ...body,
            phoneNumber: formatNewOpportunityPhoneNumber(body.phoneNumber),
        },
    });
    return response;
};

export const updateNewOpportunityAPI = async (
    centerId: string,
    newOpportunityId: string,
    accessToken: string,
    body: Record<string, any>
): Promise<any> => {
    const data =
        typeof body.phoneNumber === 'string'
            ? {
                  ...body,
                  phoneNumber: formatNewOpportunityPhoneNumber(body.phoneNumber),
              }
            : body;
    const response = await axios({
        method: 'patch',
        url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunities/${newOpportunityId}`,
        headers: headers(accessToken),
        data,
    });
    return response;
};

export const deleteNewOpportunityAPI = async (
    centerId: string,
    newOpportunityId: string,
    accessToken: string
): Promise<any> => {
    const response = await axios({
        method: 'delete',
        url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunities/${newOpportunityId}`,
        headers: headers(accessToken),
    });
    return response;
};

export const getNewOpportunityJournalAPI = async (
    centerId: string,
    newOpportunityId: string,
    accessToken: string
): Promise<import('axios').AxiosResponse<NewOpportunityJournalResponse>> => {
    const response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunities/${newOpportunityId}/journal`,
        headers: headers(accessToken),
    });
    return response;
};

export const getNewOpportunitySummaryAPI = async (
    centerId: string,
    accessToken: string,
    params: {
        startTime?: string;
        endTime?: string;
        coachId?: string;
        categoryId?: string;
        includeActiveMembers?: string;
        includeRows?: string;
    }
): Promise<any> => {
    const response = await axios({
        method: 'get',
        url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/new-opportunities/summary`, params),
        headers: headers(accessToken),
    });
    return response;
};

export const getSuperAdminNewOpportunitySummaryAPI = async (
    superAdminId: string,
    accessToken: string,
    params: {
        centerId?: string;
        centerIds?: string;
        startTime?: string;
        endTime?: string;
        coachId?: string;
        categoryId?: string;
        includeActiveMembers?: string;
        includeRows?: string;
    }
): Promise<any> => {
    const request = (requestParams: typeof params) =>
        axios({
            method: 'get',
            url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/superadmins/${superAdminId}/new-opportunities/summary`, requestParams),
            headers: headers(accessToken),
        });

    try {
        return await request(params);
    } catch (error: any) {
        const message = error?.response?.data?.message || error?.message || '';
        if (params.includeRows !== undefined && error?.response?.status === 400 && String(message).includes('includeRows')) {
            const { includeRows, ...retryParams } = params;
            return await request(retryParams);
        }
        throw error;
    }
};
